import { State, Action, StateContext, Store } from '@ngxs/store';
import { Injectable, inject } from '@angular/core';
import { patch, append, updateItem, removeItem } from '@ngxs/store/operators';

import { HardwareProduct } from '@shared/models/hardware-product.model';
import { StateHelperService } from '@core/services/state-helper.service';
import { ProductStartIndex } from '@shared/models/product.model';
import { ProductClassifications } from '@core/enums/product-classifications';
import {
  AccessoryProductAddAction,
  AccessoryProductRemoveConfirmAction,
  AccessoryProductResetAction,
  SetPaymentTypeOnAccessoryAction,
  SetNewVoiceProductOnAccessoryProductAction,
  SetVoiceProductOnAccessoryProductAction,
  RemoveAccessoryProductAction,
} from './accessory-product.actions';
import { RemoveShippingItemAction, AddShippingItemAction } from '../logistics/logistics.actions';
import { IconNames } from '@shared/enums/icon-names';
import {
  ProductAddAction,
  ProductRemoveConfirmAction,
  ResetAdditionalServicesChangesOnExistingSubscriptionAction,
  SetAccessoryProductIdOnVoiceProductAction,
  SetSelectedStepIndexAction,
  VoiceProductUpdateAction,
} from '../customer-product/customer-product.actions';
import { VoiceProduct } from '@app/shared/models/voice-product.model';
import { CustomerProductSelectors } from '../customer-product/customer-product.selectors';
import { VoiceSubscriptionOffering } from '@app/sales/modules/subscription/models/offerings-response.model';
import { AddVoiceSubscriptionForAccessoryToFormStateAction } from '../form/form.actions';

export interface CustomerProductsAccessoryStateModel {
  products: HardwareProduct[];
  isLoadingProducts: boolean;
  selectedHardwareId: string;
}

const initialCustomerProductsHardwareState = {
  products: [],
  isLoadingProducts: false,
  selectedHardwareId: null,
};

@State<CustomerProductsAccessoryStateModel>({
  name: 'customerProductsAccessory',
  defaults: initialCustomerProductsHardwareState,
})
@Injectable()
export class CustomerProductHardwareState {
  private stateHelper = inject(StateHelperService);
  private store = inject(Store);

  @Action(AccessoryProductAddAction)
  public hardwareProductAdd(
    { getState, setState, dispatch }: StateContext<CustomerProductsAccessoryStateModel>,
    { hardwareProduct }: AccessoryProductAddAction
  ) {
    const state = getState();
    const existingVoiceProducts = this.store.selectSnapshot<VoiceProduct[]>(
      CustomerProductSelectors.getExistingVoiceProducts
    );

    if (!hardwareProduct.id) {
      hardwareProduct.id = this.stateHelper.getIncrementedProductId(state.products, ProductStartIndex.Hardware);
      hardwareProduct.isExistingSubscription = false;
    }

    setState(patch({ products: append([hardwareProduct]) }));

    if (hardwareProduct.selectedSubscription && !hardwareProduct.subscriptionIdToUpgrade) {
      this.store.dispatch(
        new SetNewVoiceProductOnAccessoryProductAction(
          hardwareProduct.id,
          hardwareProduct.selectedSubscription.subscriptionOfferingId
        )
      );
    }

    if (hardwareProduct.subscriptionIdToUpgrade) {
      const existingVoiceProduct = existingVoiceProducts.find(p => p.id === hardwareProduct.subscriptionIdToUpgrade);
      const newOfferingId = hardwareProduct.selectedSubscription?.subscriptionOfferingId;
      const newAllowedChangedInfo = existingVoiceProduct.allowedSubscriptionOfferings.find(
        o => o.subscriptionOffering.id === newOfferingId
      );

      this.store.dispatch([
        new VoiceProductUpdateAction(
          existingVoiceProduct.id,
          newAllowedChangedInfo.subscriptionOffering,
          newAllowedChangedInfo.changeInfo
        ),
        new ResetAdditionalServicesChangesOnExistingSubscriptionAction(existingVoiceProduct.id),
        new SetVoiceProductOnAccessoryProductAction(hardwareProduct.id, existingVoiceProduct.id),
        new SetAccessoryProductIdOnVoiceProductAction(existingVoiceProduct.id, hardwareProduct.id),
      ]);
    }

    if (!hardwareProduct.equipment.isManualRegistration && hardwareProduct.equipment.offeringId) {
      dispatch(
        new AddShippingItemAction({
          id: hardwareProduct.id,
          name: hardwareProduct.equipment.name,
          offeringId: hardwareProduct.equipment.offeringId,
          productClassification: ProductClassifications.Equipment,
          image: hardwareProduct.equipment.image,
          icon: IconNames.ice_power,
        })
      );
    }
  }

  @Action(AccessoryProductRemoveConfirmAction)
  public accessoryProductRemoveConfirm(
    { getState, patchState, dispatch }: StateContext<CustomerProductsAccessoryStateModel>,
    { productId }: AccessoryProductRemoveConfirmAction
  ) {
    const state = getState();
    const product = state.products.find(p => p.id === productId);
    if (product.voiceProductId) {
      dispatch(new ProductRemoveConfirmAction(product.voiceProductId)).subscribe(() => {
        const reducedCustomerProducts = state.products.filter(p => p.id !== productId);

        patchState({ products: reducedCustomerProducts });
      });
    } else {
      const reducedCustomerProducts = state.products.filter(p => p.id !== productId);

      patchState({ products: reducedCustomerProducts });
    }

    dispatch(new RemoveShippingItemAction(productId, ProductClassifications.Equipment));
  }

  @Action(SetPaymentTypeOnAccessoryAction)
  public SetPaymentTypeAction(
    { setState }: StateContext<CustomerProductsAccessoryStateModel>,
    { productId, paymentType }: SetPaymentTypeOnAccessoryAction
  ) {
    setState(
      patch<CustomerProductsAccessoryStateModel>({
        products: updateItem<HardwareProduct>(
          product => product.id === productId,
          patch<HardwareProduct>({ paymentType: paymentType })
        ),
      })
    );
  }

  @Action(AccessoryProductResetAction)
  public hardwareProductReset({ setState }: StateContext<CustomerProductsAccessoryStateModel>) {
    setState(initialCustomerProductsHardwareState);
  }

  @Action(SetNewVoiceProductOnAccessoryProductAction)
  public setNewVoiceProductOnAccessoryProduct(
    { dispatch }: StateContext<CustomerProductsAccessoryStateModel>,
    { productId, offeringId }: SetNewVoiceProductOnAccessoryProductAction
  ) {
    this.store.dispatch(new ProductAddAction(null, null, productId)).subscribe(() => {
      const allVoiceProducts = this.store.selectSnapshot<VoiceSubscriptionOffering[]>(
        CustomerProductSelectors.getOfferingsIncludingCustomerBTL
      );
      let voiceProduct = this.store
        .selectSnapshot<VoiceProduct[]>(CustomerProductSelectors.getAllVoiceProducts)
        .find(p => p.accessoryProductId === productId);
      const newVoiceOffering = allVoiceProducts.find(o => o.id === offeringId);
      if (voiceProduct) {
        return dispatch(new VoiceProductUpdateAction(voiceProduct.id, newVoiceOffering, null)).subscribe(() => {
          voiceProduct = this.store
            .selectSnapshot<VoiceProduct[]>(CustomerProductSelectors.getAllVoiceProducts)
            .find(p => p.accessoryProductId === productId);

          if (!voiceProduct.isExistingSubscription) {
            dispatch(new SetSelectedStepIndexAction(1));
            dispatch(new AddVoiceSubscriptionForAccessoryToFormStateAction(voiceProduct));
          }
          dispatch(new SetVoiceProductOnAccessoryProductAction(productId, voiceProduct.id));
        });
      }
    });
  }

  @Action(SetVoiceProductOnAccessoryProductAction)
  public setVoiceProductOnAccessoryProduct(
    { setState }: StateContext<CustomerProductsAccessoryStateModel>,
    { productId, voiceProductId }: SetVoiceProductOnAccessoryProductAction
  ) {
    setState(
      patch<CustomerProductsAccessoryStateModel>({
        products: updateItem<HardwareProduct>(
          product => product.id === productId,
          patch<HardwareProduct>({ voiceProductId: voiceProductId })
        ),
      })
    );
  }

  @Action(RemoveAccessoryProductAction)
  public removeAccessoryProduct(
    { setState }: StateContext<CustomerProductsAccessoryStateModel>,
    { productId }: RemoveAccessoryProductAction
  ) {
    setState(
      patch<CustomerProductsAccessoryStateModel>({
        products: removeItem(item => item.id === productId),
      })
    );
  }
}
